/**
 * Settings management with localStorage persistence
 * Supports both global and per-game settings
 */

export class Settings {
    private static _getGameKey(): string {
        // Get current URL, remove /index.html if present, use as game key
        let url = window.location.href;
        url = url.replace(/\/index\.html$/, '');
        // Extract pathname and use as key
        try {
            const urlObj = new URL(url);
            return urlObj.pathname.replace(/\/index\.html$/, '') || 'default';
        } catch {
            return 'default';
        }
    }

    private static _storageName(name: string, global: boolean = false): string {
        if (global) {
            return 'regalia_global_' + name;
        } else {
            const gameKey = this._getGameKey();
            return `regalia_game_${gameKey}_${name}`;
        }
    }

    private static _get(name: string, defValue: any, global: boolean = false): any {
        // Try per-game first, then global as fallback
        if (!global) {
            const gameValue = localStorage.getItem(this._storageName(name, false));
            if (gameValue !== null) {
                return JSON.parse(gameValue);
            }
            // Fallback to global
            const globalValue = localStorage.getItem(this._storageName(name, true));
            if (globalValue !== null) {
                return JSON.parse(globalValue);
            }
        } else {
            const value = localStorage.getItem(this._storageName(name, true));
            if (value !== null) {
                return JSON.parse(value);
            }
        }
        return defValue;
    }

    private static _set(name: string, value: any, global: boolean = false): void {
        localStorage.setItem(this._storageName(name, global), JSON.stringify(value));
        // Note: Storage events are automatically fired by the browser for other tabs
        // when localStorage changes. We don't need to manually dispatch them.
    }

    static addBoolSetting(name: string, defValue: boolean, global: boolean = false): void {
        if ((this as any)[name] !== undefined) {
            throw new Error('Setting with name "' + name + '" already exists!');
        }

        let value = this._get(name, !!defValue, global);
        const storageName = this._storageName(name, global);
        Object.defineProperty(this, name, {
            get() {
                return value;
            },
            set(newValue: boolean) {
                Settings._set(name, value = !!newValue, global);
                // Dispatch change event for this setting
                if (global) {
                    window.dispatchEvent(new CustomEvent('settingsChanged', {
                        detail: { name, value: !!newValue, global }
                    }));
                }
            },
            enumerable: true,
        });
        
        // Listen for storage events (cross-tab sync for global settings)
        if (global) {
            window.addEventListener('storage', (e: StorageEvent) => {
                if (e.key === storageName && e.newValue !== null) {
                    const newValue = JSON.parse(e.newValue);
                    if (value !== newValue) {
                        value = !!newValue;
                        // Dispatch change event
                        window.dispatchEvent(new CustomEvent('settingsChanged', {
                            detail: { name, value: !!newValue, global }
                        }));
                    }
                }
            });
        }
    }

    static addNumberSetting(name: string, defValue: number, global: boolean = false): void {
        if ((this as any)[name] !== undefined) {
            throw new Error('Setting with name "' + name + '" already exists!');
        }

        function parseNumberSafe(value: any): number {
            const num = parseFloat(String(value));
            return isNaN(num) ? 0 : num;
        }

        let value = parseNumberSafe(this._get(name, defValue, global));
        const storageName = this._storageName(name, global);
        Object.defineProperty(this, name, {
            get() {
                return value;
            },
            set(newValue: number) {
                Settings._set(name, value = parseNumberSafe(newValue), global);
                // Dispatch change event for this setting
                if (global) {
                    window.dispatchEvent(new CustomEvent('settingsChanged', {
                        detail: { name, value: parseNumberSafe(newValue), global }
                    }));
                }
            },
            enumerable: true,
        });
        
        // Listen for storage events (cross-tab sync for global settings)
        if (global) {
            window.addEventListener('storage', (e: StorageEvent) => {
                if (e.key === storageName && e.newValue !== null) {
                    const newValue = parseNumberSafe(JSON.parse(e.newValue));
                    if (value !== newValue) {
                        value = newValue;
                        // Dispatch change event
                        window.dispatchEvent(new CustomEvent('settingsChanged', {
                            detail: { name, value: newValue, global }
                        }));
                    }
                }
            });
        }
    }

    static addIntSetting(name: string, defValue: number, global: boolean = false): void {
        if ((this as any)[name] !== undefined) {
            throw new Error('Setting with name "' + name + '" already exists!');
        }

        function parseIntSafe(value: any): number {
            const num = parseInt(String(value), 10);
            return isNaN(num) ? 0 : num;
        }

        let value = parseIntSafe(this._get(name, defValue, global));
        const storageName = this._storageName(name, global);
        Object.defineProperty(this, name, {
            get() {
                return value;
            },
            set(newValue: number) {
                Settings._set(name, value = parseIntSafe(newValue), global);
                // Dispatch change event for this setting
                if (global) {
                    window.dispatchEvent(new CustomEvent('settingsChanged', {
                        detail: { name, value: parseIntSafe(newValue), global }
                    }));
                }
            },
            enumerable: true,
        });
        
        // Listen for storage events (cross-tab sync for global settings)
        if (global) {
            window.addEventListener('storage', (e: StorageEvent) => {
                if (e.key === storageName && e.newValue !== null) {
                    const newValue = parseIntSafe(JSON.parse(e.newValue));
                    if (value !== newValue) {
                        value = newValue;
                        // Dispatch change event
                        window.dispatchEvent(new CustomEvent('settingsChanged', {
                            detail: { name, value: newValue, global }
                        }));
                    }
                }
            });
        }
    }
}

// Initialize default settings
// All settings are now global (apply to all games)
const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
Settings.addBoolSetting('darkMode', prefersDark, true); // Global
Settings.addBoolSetting('historyEnabled', true, true); // Global
Settings.addIntSetting('historySize', 100, true); // Global
Settings.addIntSetting('musicVolume', 100, true); // Global
Settings.addIntSetting('sfxVolume', 100, true); // Global
Settings.addBoolSetting('debugEnabled', false, true); // Global
Settings.addBoolSetting('enablePreloading', true, true); // Global

Settings.addIntSetting('saveSortField', 0, true); // Global
Settings.addBoolSetting('saveSortAsc', false, true); // Global

// Split panel sizes (percentages)
Settings.addNumberSetting('splitRoomPicPanel', 40, true); // Room panel width percentage (default 40%)
Settings.addNumberSetting('splitMainPlayerRow', 70, true); // Main row height percentage (default 70%)
Settings.addNumberSetting('splitCompassRoomObjects', 40, true); // Compass/portrait width percentage (default 40%)
Settings.addNumberSetting('splitRoomObjectsVisibleChars', 33.33, true); // Room objects width percentage (default 33.33%)
Settings.addNumberSetting('splitVisibleCharsInventory', 33.33, true); // Visible chars width percentage (default 33.33% for equal widths)

// Expose Settings globally
(window as any).Settings = Settings;